import React, { useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import InputSearch from '../InputSearch';
import { Produto, ProdutoReserva } from '../../../types';
import { formatPrice } from '../../../utils/priceUtils';

interface InputSearchProductsProps {
  produtos: Produto[];
  selected?: ProdutoReserva[];
  initialSelected?: ProdutoReserva[];
  onSelectedChange?: (items: ProdutoReserva[]) => void;
  size?: number;
  labelSelected?: string;
  maxListHeight?: string;
}

export const InputSearchProducts: React.FC<InputSearchProductsProps> = ({
  produtos,
  selected,
  initialSelected,
  onSelectedChange,
  size = 12,
  labelSelected = 'Produtos Selecionados',
  maxListHeight = '400px',
}) => {
  const [items, setItems] = useState<ProdutoReserva[]>(selected ?? initialSelected ?? []);
  const isControlled = selected !== undefined;

  useEffect(() => {
    if (isControlled) {
      setItems(selected as ProdutoReserva[]);
    }
  }, [selected, isControlled]);

  const setItemsAndNotify = (newItems: ProdutoReserva[]) => {
    if (!isControlled) {
      setItems(newItems);
    }
    onSelectedChange?.(newItems);
  };

  const addProduto = (produto: Produto) => {
    if (!produto || !produto.id) return;

    const exists = items.some(
      (p) =>
        p.produtoId === produto.id &&
        p.produtoTamanho === produto.tamanho &&
        p.produtoCor === produto.cor
    );

    if (exists) {
      const updated = items.map((p) => {
        if (
          p.produtoId === produto.id &&
          p.produtoTamanho === produto.tamanho &&
          p.produtoCor === produto.cor
        ) {
          const maxQtd = Math.max(1, produtos.find((pr) => pr.id === produto.id)?.quantidade || 1);
          const nextQtd = Math.min(p.quantidade + 1, maxQtd);
          const unit = p.precoUnitario ?? produto.preco ?? 0;
          return {
            ...p,
            quantidade: nextQtd,
            precoUnitario: unit,
            precoTotal: unit * nextQtd,
          };
        }
        return p;
      });
      setItemsAndNotify(updated);
      return;
    }

    const novo: ProdutoReserva = {
      produtoId: produto.id!,
      produtoTitulo: produto.titulo,
      produtoTamanho: produto.tamanho,
      produtoCor: produto.cor,
      produtoCodigoProduto: produto.codigoProduto,
      quantidade: 1,
      precoUnitario: produto.preco || 0,
      precoTotal: produto.preco || 0,
      produtoObservacoes: '',
    };
    setItemsAndNotify([...items, novo]);
  };

  const removeProduto = (produtoId: string, tamanho: string, cor: string) => {
    const updated = items.filter(
      (p) => !(p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor)
    );
    setItemsAndNotify(updated);
  };

  const updateQuantidade = (
    produtoId: string,
    tamanho: string,
    cor: string,
    quantidade: number
  ) => {
    const prod = produtos.find((p) => p.id === produtoId);
    const maxQtd = prod?.quantidade ?? 1;
    const finalQtd = Math.max(1, Math.min(quantidade, maxQtd));
    const updated = items.map((p) => {
      if (p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor) {
        const unit = p.precoUnitario ?? 0;
        return { ...p, quantidade: finalQtd, precoTotal: unit * finalQtd };
      }
      return p;
    });
    setItemsAndNotify(updated);
  };

  const updateObservacoes = (
    produtoId: string,
    tamanho: string,
    cor: string,
    obs: string
  ) => {
    const updated = items.map((p) => {
      if (p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor) {
        return { ...p, produtoObservacoes: obs.toUpperCase() };
      }
      return p;
    });
    setItemsAndNotify(updated);
  };

  const totalItens = items.reduce((sum, p) => sum + (p.precoTotal || 0), 0);

  return (
    <>
      <InputSearch
        placeholder="Buscar produto por nome, tamanho ou cor..."
        name="produto"
        data={produtos}
        value={null}
        onChange={(produto: Produto) => addProduto(produto)}
        size={size}
      />

      {items.length > 0 && (
        <div className="mt-3">
          <div className="form-label">{labelSelected}</div>
          <div className="border rounded p-3" >
            {items.map((item, index) => (
              <div
                key={`${item.produtoId}-${item.produtoTamanho}-${item.produtoCor}-${index}`}
                className="d-flex flex-wrap align-items-center mb-2 p-2 bg-light rounded"
              >
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2">
                    {item.produtoCodigoProduto && (
                      <span
                        className="fw-bold text-primary"
                        style={{ fontSize: '12px', fontFamily: 'monospace' }}
                      >
                        [{item.produtoCodigoProduto}]
                      </span>
                    )}
                    <div className="fw-bold">{item.produtoTitulo}</div>
                  </div>
                  <small className="text-muted">
                    {item.produtoTamanho} - {item.produtoCor}
                  </small>
                  {item.precoUnitario && item.precoUnitario > 0 && (
                    <div className="text-success">{formatPrice(item.precoUnitario)} cada</div>
                  )}
                  <div className="d-flex flex-column gap-2 mt-2">
                    <Form.Control
                      type="text"
                      placeholder="Observações específicas deste produto..."
                      value={item.produtoObservacoes || ''}
                      onChange={(e) =>
                        updateObservacoes(
                          item.produtoId,
                          item.produtoTamanho,
                          item.produtoCor,
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>

                <div className="d-flex gap-2 mt-2 w-100 flex-wrap" style={{ minWidth: '300px' }}>
                  <div className="d-flex align-items-center gap-2 justify-content-end">
                    <Form.Control
                      type="number"
                      min="1"
                      max={produtos.find((p) => p.id === item.produtoId)?.quantidade || 1}
                      value={item.quantidade}
                      onChange={(e) =>
                        updateQuantidade(
                          item.produtoId,
                          item.produtoTamanho,
                          item.produtoCor,
                          parseInt(e.target.value) || 1
                        )
                      }
                      style={{ width: '80px', height: '40px' }}
                    />
                    <Button
                      variant="danger"
                      onClick={() =>
                        removeProduto(item.produtoId, item.produtoTamanho, item.produtoCor)
                      }
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {items.some((p) => p.precoUnitario && p.precoUnitario > 0) && (
              <div className="mt-2 pt-2 border-top text-end">
                <strong>Total dos Produtos: {formatPrice(totalItens)}</strong>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InputSearchProducts;